import { ca } from '@cian/analytics';
import { EMyHomeEstimationTargets } from '../types/estimationTarget';

interface IMyHomeAnalyticsProps {
  estimationTarget: EMyHomeEstimationTargets;
  value: number | string;
  auto: boolean;
}

export interface ITrackAddNewFlatByNumberParameters {
  autofiiled?: {
    roomsnumber?: number;
    area?: number;
  };
  result: {
    address: string;
    flatnumber?: number;
    roomsnumber?: number;
    area?: number;
    landarea?: number;
  };
}

export interface ITrackAddNewMyHomeObjectProps {
  estimationTarget: EMyHomeEstimationTargets;
  label: string;
  parameters: ITrackAddNewFlatByNumberParameters;
}

const caEventCategories: { [K in EMyHomeEstimationTargets]: string } = {
  [EMyHomeEstimationTargets.flat]: 'MyHomeFlat',
  [EMyHomeEstimationTargets.house]: 'MyHomeLand',
  [EMyHomeEstimationTargets.land]: 'MyHomeHouse',
};

const caEstimationTargetLabels: { [K in EMyHomeEstimationTargets]: string } = {
  [EMyHomeEstimationTargets.flat]: 'flat',
  [EMyHomeEstimationTargets.house]: 'house',
  [EMyHomeEstimationTargets.land]: 'land',
};

export function trackMyHomeEstimationTargetSelect(estimationTarget: EMyHomeEstimationTargets) {
  ca('eventSite', {
    name: 'oldevent',
    category: caEventCategories[estimationTarget],
    action: 'SelectEstimateType_sopr',
    label: caEstimationTargetLabels[estimationTarget],
  });
}

export function prepareTrackMyHomeAddressPick() {
  return (rawAddress: string, suggestedAddress: string, estimationTarget: EMyHomeEstimationTargets) => {
    ca('eventSite', {
      name: 'oldevent',
      category: caEventCategories[estimationTarget],
      action: 'TypeIn_sopr',
      label: 'FullAddress',
      page: {
        extra: {
          rawAddress,
          suggestedAddress,
        },
      },
    });
  };
}

export function trackMyHomeAddressInputClick(estimationTarget: EMyHomeEstimationTargets) {
  ca('eventSite', {
    name: 'oldevent',
    category: caEventCategories[estimationTarget],
    action: 'Click_sopr',
    label: 'Address',
  });
}

export function trackMyHomeAddressInputLaterSomeSeconds(label: string, estimationTarget: EMyHomeEstimationTargets) {
  ca('eventSite', {
    name: 'oldevent',
    category: caEventCategories[estimationTarget],
    action: 'InputTimeout',
    label,
  });
}

export function trackMyHomeFlatNumberPick({ value, auto }: Partial<IMyHomeAnalyticsProps>) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'MyHomeFlat',
    action: 'TypeIn_sopr',
    label: 'FlatNumber',
    page: {
      pageType: 'MyHomeMainPage',
      siteType: 'desktop',
      extra: {
        value,
        isAvailable: auto,
      },
    },
  });
}

export function trackMyHomeRoomsCountClick({ value, auto }: Partial<IMyHomeAnalyticsProps>) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'MyHomeFlat',
    action: 'Click_sopr',
    label: 'RoomsNumber',
    page: {
      pageType: 'MyHomeMainPage',
      siteType: 'desktop',
      extra: {
        value,
        auto,
      },
    },
  });
}

export function getTrackMyHomeAreaPick({ estimationTarget, value, auto }: IMyHomeAnalyticsProps) {
  return () => {
    ca('eventSite', {
      name: 'oldevent',
      category: caEventCategories[estimationTarget],
      action: 'TypeIn_sopr',
      label: 'Area',
      page: {
        pageType: 'MyHomeMainPage',
        siteType: 'desktop',
        extra: {
          value,
          auto,
        },
      },
    });
  };
}

export function getTrackMyHomeLandAreaPick(estimationTarget: EMyHomeEstimationTargets) {
  return () => {
    ca('eventSite', {
      name: 'oldevent',
      category: caEventCategories[estimationTarget],
      action: 'TypeIn_sopr',
      label: 'LandArea',
    });
  };
}

export function trackAddNewMyHomeObject({ label, estimationTarget, parameters }: ITrackAddNewMyHomeObjectProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: caEventCategories[estimationTarget],
    action: 'Click',
    label,
    page: {
      extra: {
        parameters,
      },
    },
  });
}
